// "Add to Calendar" helpers — fully client-side. No accounts, no backend.
// Produces a Google Calendar link and a downloadable .ics (Apple / Outlook).

export interface CalendarEvent {
  title: string
  description?: string
  location?: string
  start: Date
  end?: Date // defaults to start + 1 hour
  uid?: string
}

function pad(n: number): string { return String(n).padStart(2, '0') }

// UTC timestamp in iCal basic format: YYYYMMDDTHHMMSSZ
function toICalUtc(d: Date): string {
  return (
    d.getUTCFullYear().toString() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) +
    'T' +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds()) +
    'Z'
  )
}

function endOrDefault(evt: CalendarEvent): Date {
  return evt.end ?? new Date(evt.start.getTime() + 60 * 60 * 1000)
}

export function googleCalendarUrl(evt: CalendarEvent): string {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: evt.title,
    dates: `${toICalUtc(evt.start)}/${toICalUtc(endOrDefault(evt))}`,
  })
  if (evt.location) params.set('location', evt.location)
  if (evt.description) params.set('details', evt.description)
  return `https://calendar.google.com/calendar/render?${params.toString()}`
}

// Escape text per RFC 5545
function icsEscape(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n')
}

export function buildIcs(evt: CalendarEvent): string {
  const uid = evt.uid || `${Date.now()}@socialbrew`
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Social Brew//Coffee Date//EN',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${toICalUtc(new Date())}`,
    `DTSTART:${toICalUtc(evt.start)}`,
    `DTEND:${toICalUtc(endOrDefault(evt))}`,
    `SUMMARY:${icsEscape(evt.title)}`,
  ]
  if (evt.location) lines.push(`LOCATION:${icsEscape(evt.location)}`)
  if (evt.description) lines.push(`DESCRIPTION:${icsEscape(evt.description)}`)
  lines.push('END:VEVENT', 'END:VCALENDAR')
  return lines.join('\r\n')
}

// Trigger the .ics "Add to Calendar" flow (Apple Calendar / Outlook / import).
export function downloadIcs(evt: CalendarEvent, filename = 'coffee-date.ics'): void {
  const blob = new Blob([buildIcs(evt)], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 2000)
}

export function openGoogleCalendar(evt: CalendarEvent): void {
  window.open(googleCalendarUrl(evt), '_blank', 'noopener')
}
